/**
 * v3.21-1716 · Deal Workspace (full-page)
 *
 * Replaces the narrow right-side drawer from v1715.  ONE ROW = ONE DEAL
 * lands the operator on this full-width workspace.  70/30 layout:
 *
 *   ┌───────────────────────────────────────────┬─────────────────────┐
 *   │ MAIN                                       │ RIGHT COLUMN        │
 *   │  • Overview  • Ownership  • Financing      │  • Next Action      │
 *   │  • Client Info & Service Auth              │  • Missing Info     │
 *   │  • Contract / Agreements                   │  • Document Status  │
 *   │  • Closing Packet  • Payments  • Activity  │  • Recent Activity  │
 *   │                                            │  • Ask Ghost        │
 *   └───────────────────────────────────────────┴─────────────────────┘
 *
 * The Deals queue remains at `/ptc-deals`; clicking a row navigates
 * here at `/ptc-deals/:dealId`.  Authenticated PDF viewing is done
 * inside this page — the browser NEVER navigates directly to a
 * bearer-auth endpoint.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AuthenticatedPdfViewer } from '../components/AuthenticatedPdfViewer';
import {
  ArrowLeft, Loader2, FileText, User, Home, DollarSign, ClipboardList,
  Activity as ActivityIcon, Sparkles, ExternalLink, RefreshCw, AlertTriangle,
  CheckCircle2, Circle, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import useDocumentTitle from '../hooks/useDocumentTitle';

const money = v => v != null && v !== '' ? `$${Number(v).toLocaleString(undefined,{maximumFractionDigits:0})}` : '—';
const shortId = v => v ? String(v).slice(0, 8) : '—';

const PHASE_ORDER = [
  { key: 'intake',       label: 'Intake',           stages: ['new_lead','contacted'] },
  { key: 'ownership',    label: 'Ownership',        stages: ['credibility_sent','info_request_sent'] },
  { key: 'service_auth', label: 'Service Auth',     stages: ['info_received'] },
  { key: 'agreement',    label: 'Agreement',        stages: ['proposal_sent','contract_sent','contract_signed'] },
  { key: 'closing',      label: 'Closing',          stages: ['payment_setup','payment_in_progress'] },
  { key: 'settlement',   label: 'Settlement',       stages: ['threshold_met','submitted_to_partner','closed'] },
];

function phaseForStage(stage) {
  for (const p of PHASE_ORDER) if (p.stages.includes(stage)) return p.key;
  return 'intake';
}


// v3.21-1716 · Missing Information editor — groups needs_input by
// authoritative write-target (Client SSOT / Deal SSOT / Ownership
// SSOT / Document-specific override).  Never blindly writes
// document-only values into Client SSOT.  Save recomputes
// classification + stage/next_action so the workspace stays coherent.
function MissingInfoEditor({ client, deal, ownerships, needs, onSaved }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Field routing table
  const FIELD_MAP = {
    // key: { label, group, target, target_field, ownership_slot? }
    client_1_name:            { label: 'Client Name',       group: 'CLIENT',            target: 'client', field: 'full_name' },
    client_1_dob:             { label: 'Client DOB',        group: 'CLIENT',            target: 'client', field: 'dob' },
    client_1_ssn:             { label: 'Client SSN',        group: 'CLIENT',            target: 'client', field: 'ssn' },
    client_1_mobile:          { label: 'Client Mobile',     group: 'CLIENT',            target: 'client', field: 'phone' },
    client_1_email:           { label: 'Client Email',      group: 'CLIENT',            target: 'client', field: 'email' },
    mailing_address_street:   { label: 'Street',            group: 'MAILING ADDRESS',   target: 'client', field: 'street_address' },
    mailing_address_city:     { label: 'City',              group: 'MAILING ADDRESS',   target: 'client', field: 'city' },
    mailing_address_state:    { label: 'State',             group: 'MAILING ADDRESS',   target: 'client', field: 'state' },
    mailing_address_zip:      { label: 'ZIP',               group: 'MAILING ADDRESS',   target: 'client', field: 'zip_code' },
    total_service_fee:        { label: 'Total Service Fee', group: 'SERVICE / PAYMENT', target: 'deal',   field: 'price' },
    amount_due_at_signing:    { label: 'Amount Due at Signing', group: 'SERVICE / PAYMENT', target: 'deal', field: 'amount_due_at_signing' },
    payment_method:           { label: 'Payment Method',    group: 'SERVICE / PAYMENT', target: 'deal',   field: 'payment_method' },
    payment_schedule:         { label: 'Payment Schedule',  group: 'SERVICE / PAYMENT', target: 'deal',   field: 'payment_schedule' },
    ptc_intake_specialist:    { label: 'Intake Specialist', group: 'INTERNAL',          target: 'override' },
    ptc_manager:              { label: 'Manager',           group: 'INTERNAL',          target: 'client', field: 'ptc_manager_name' },
    timeshare_1_resort_developer: { label: 'Resort / Developer (TS1)', group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'resort_name' },
    timeshare_1_account_number:   { label: 'Account # (TS1)',         group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'account_number' },
    timeshare_1_mortgage_status:  { label: 'Mortgage Status (TS1)',   group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'mortgage_status' },
    timeshare_1_mortgage_balance: { label: 'Mortgage Balance (TS1)',  group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'loan_current_balance' },
    timeshare_1_resort_phone:     { label: 'Resort Phone (TS1)',      group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'resort_phone' },
    timeshare_1_resort_address:   { label: 'Resort Address (TS1)',    group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'resort_address' },
    timeshare_1_resort_city:      { label: 'Resort City (TS1)',       group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'resort_city' },
    timeshare_1_resort_state:     { label: 'Resort State (TS1)',      group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'resort_state' },
    timeshare_1_resort_zip:       { label: 'Resort ZIP (TS1)',        group: 'OWNERSHIP', target: 'ownership', slot: 0, field: 'resort_zip' },
    timeshare_2_resort_developer: { label: 'Resort / Developer (TS2)', group: 'OWNERSHIP', target: 'ownership', slot: 1, field: 'resort_name' },
    timeshare_2_account_number:   { label: 'Account # (TS2)',         group: 'OWNERSHIP', target: 'ownership', slot: 1, field: 'account_number' },
    timeshare_2_mortgage_status:  { label: 'Mortgage Status (TS2)',   group: 'OWNERSHIP', target: 'ownership', slot: 1, field: 'mortgage_status' },
    timeshare_2_mortgage_balance: { label: 'Mortgage Balance (TS2)',  group: 'OWNERSHIP', target: 'ownership', slot: 1, field: 'loan_current_balance' },
    special_instructions:         { label: 'Special Instructions',   group: 'INTERNAL',  target: 'deal',      field: 'notes' },
  };
  const GROUP_ORDER = ['CLIENT', 'MAILING ADDRESS', 'OWNERSHIP', 'SERVICE / PAYMENT', 'INTERNAL'];

  const groups = {};
  needs.forEach(key => {
    const meta = FIELD_MAP[key];
    if (!meta) return;
    (groups[meta.group] = groups[meta.group] || []).push({ key, ...meta });
  });

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      client_updates:    {},
      deal_updates:      {},
      ownership_updates: {},
      document_overrides: {},
    };
    for (const [key, raw] of Object.entries(values)) {
      const v = String(raw ?? '').trim();
      if (!v) continue;
      const meta = FIELD_MAP[key];
      if (!meta) continue;
      if (meta.target === 'client')   payload.client_updates[meta.field] = v;
      else if (meta.target === 'deal')     payload.deal_updates[meta.field] = v;
      else if (meta.target === 'override') payload.document_overrides[key] = v;
      else if (meta.target === 'ownership') {
        const own = ownerships[meta.slot];
        if (own?.id) {
          payload.ownership_updates[own.id] = payload.ownership_updates[own.id] || {};
          payload.ownership_updates[own.id][meta.field] = v;
        }
      }
    }
    try {
      await api.post(`/ghost-deal/missing-info/${client.id}/${deal.id}/save`, payload);
      toast.success('Missing information saved');
      setValues({});
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const filled = Object.values(values).filter(v => String(v ?? '').trim() !== '').length;

  return (
    <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs space-y-3" data-testid="sa-needs-input">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-amber-800 flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5" />
          Missing Information ({needs.length})
        </div>
        <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
                onClick={handleSave} disabled={saving || filled === 0}
                data-testid="mi-save-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
          Save {filled > 0 ? `(${filled})` : ''}
        </Button>
      </div>
      {GROUP_ORDER.map(g => {
        const items = groups[g];
        if (!items?.length) return null;
        return (
          <div key={g} className="bg-white border border-amber-100 rounded p-2 space-y-1" data-testid={`mi-group-${g.replace(/[^a-z]/gi,'').toLowerCase()}`}>
            <div className="text-[10px] uppercase text-slate-500 font-semibold">{g}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items.map(it => (
                <label key={it.key} className="text-[11px]">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-slate-600">{it.label}</span>
                    <Badge variant="outline" className="text-[9px] py-0 h-4">
                      {it.target === 'override' ? 'Doc-only' : `→ ${it.target}`}
                    </Badge>
                  </div>
                  <Input
                    className="h-7 text-xs"
                    value={values[it.key] || ''}
                    onChange={e => setValues(v => ({ ...v, [it.key]: e.target.value }))}
                    data-testid={`mi-input-${it.key}`}
                    placeholder="Enter value…"
                  />
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DealWorkspacePage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  useDocumentTitle('Deal Workspace');

  const [loading, setLoading] = useState(true);
  const [deal, setDeal] = useState(null);
  const [client, setClient] = useState(null);
  const [ownerships, setOwnerships] = useState([]);
  const [saContext, setSaContext] = useState(null);        // service-auth ctx
  const [revisions, setRevisions] = useState([]);          // service-auth revisions
  const [activities, setActivities] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInfo, setPreviewInfo] = useState({ path: '', title: '', filename: '' });
  const [tab, setTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const d = await api.get(`/ptc-deals/${dealId}`).catch(() => null);
      const dealDoc = d?.data || d;
      if (!dealDoc) {
        toast.error('Deal not found');
        setLoading(false);
        return;
      }
      setDeal(dealDoc);
      const cId = dealDoc.client_id;
      const [c, own] = await Promise.all([
        api.get(`/clients/${cId}`).catch(() => ({ data: null })),
        api.get(`/clients/${cId}/ownerships`).catch(() => ({ data: [] })),
      ]);
      setClient(c.data);
      setOwnerships(Array.isArray(own.data) ? own.data : (own.data?.ownerships || []));
      // Service Auth context
      try {
        const ctx = await api.get(`/clients/${cId}/deals/${dealId}/service-auth/context`);
        setSaContext(ctx.data);
      } catch (e) { setSaContext(null); }
      // Revisions
      try {
        const revs = await api.get(`/clients/${cId}/deals/${dealId}/service-auth/revisions`);
        setRevisions(revs.data?.revisions || []);
      } catch (e) { setRevisions([]); }
      // Activity
      try {
        const acts = await api.get(`/clients/${cId}/activities`).catch(() => ({ data: [] }));
        setActivities(Array.isArray(acts.data) ? acts.data : (acts.data?.activities || []));
      } catch (e) { setActivities([]); }
    } catch (e) {
      console.error(e); toast.error('Failed to load Deal Workspace');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  const refresh = () => { setRefreshing(true); load().finally(() => setRefreshing(false)); };

  const openServiceAuthPreview = () => {
    setPreviewInfo({
      path:     `/clients/${client.id}/deals/${deal.id}/service-auth/preview.pdf?mark_needs_input=true`,
      title:    'Client Info and Service Authorization — Preview',
      filename: `ServiceAuth_${deal.deal_number || 'preview'}.pdf`,
    });
    setPreviewOpen(true);
  };

  const openRevision = (revId, num) => {
    // Latest revision PDF also served by preview endpoint after Create.
    setPreviewInfo({
      path:     `/clients/${client.id}/deals/${deal.id}/service-auth/preview.pdf?mark_needs_input=false`,
      title:    `Client Info and Service Authorization · ${num || shortId(revId)}`,
      filename: `ServiceAuth_${num || shortId(revId)}.pdf`,
    });
    setPreviewOpen(true);
  };

  const createRevision = async () => {
    if (!client?.id || !deal?.id) return;
    setCreating(true);
    try {
      const r = await api.post(
        `/clients/${client.id}/deals/${deal.id}/service-auth/create`,
        { overrides: {} },
      );
      toast.success(`Service Authorization ${r.data?.revision_number} created`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.required_missing?.length || detail?.conditional_missing?.length) {
        toast.error('Missing required fields — see Service Auth section');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Create failed');
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }
  if (!deal || !client) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
        <p className="text-sm text-slate-600">Deal not found.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/ptc-deals')}>
          Back to Deals
        </Button>
      </div>
    );
  }

  const currentPhase = phaseForStage((deal.deal_stage || '').toLowerCase());
  const needsInputCount = saContext?.needs_input?.length || 0;

  // Closing packet gating (front-end mirror of backend rules).
  const closingReady = needsInputCount === 0 && revisions.length > 0;

  return (
    <div className="space-y-4" data-testid="deal-workspace">
      {/* ── STICKY HEADER ─────────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-white border-b -mx-4 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <Button size="sm" variant="ghost" className="h-8 px-2"
                  onClick={() => navigate('/ptc-deals')} data-testid="workspace-back">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Deals
          </Button>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-800 truncate" data-testid="workspace-client-name">
              {client.full_name}
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
              <span data-testid="workspace-deal-number">Deal: <b>{deal.deal_number}</b></span>
              <span>·</span>
              <span>Amount: <b>{money(deal.price || deal.total_deal_price)}</b></span>
              <span>·</span>
              <Badge variant="outline" className="text-[10px]" data-testid="workspace-stage">
                {String(deal.deal_stage || 'new_lead').replace(/_/g,' ')}
              </Badge>
              {deal.next_action && (
                <>
                  <span>·</span>
                  <span>Next: <b>{deal.next_action}</b></span>
                </>
              )}
              {(deal.advisor_name || client.sales_rep_name) && (
                <>
                  <span>·</span>
                  <span>Rep: {deal.advisor_name || client.sales_rep_name}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => navigate(`/clients/${client.id}`)}
                  data-testid="workspace-open-client">
            <User className="h-3.5 w-3.5 mr-1" />
            Open Client
          </Button>
          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs"
                  onClick={refresh}
                  data-testid="workspace-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Major-phase status rail */}
        <div className="flex items-center gap-1 mt-3 overflow-x-auto" data-testid="workspace-phase-rail">
          {PHASE_ORDER.map((p, i) => {
            const isCurrent = p.key === currentPhase;
            const passed = PHASE_ORDER.findIndex(x => x.key === currentPhase) > i;
            return (
              <div key={p.key} className="flex items-center gap-1">
                <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] ${
                  isCurrent ? 'bg-accent text-white font-semibold' :
                  passed ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'
                }`} data-testid={`workspace-phase-${p.key}`}>
                  {passed ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                  {p.label}
                </div>
                {i < PHASE_ORDER.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 70/30 BODY ─────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[7fr_3fr]">
        {/* MAIN */}
        <div className="min-w-0">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="w-full justify-start flex-wrap h-auto p-1" data-testid="workspace-tabs">
              <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
              <TabsTrigger value="ownership" className="text-xs">Ownership</TabsTrigger>
              <TabsTrigger value="service_auth" className="text-xs">Client Info &amp; Service Auth</TabsTrigger>
              <TabsTrigger value="contract" className="text-xs">Contract / Agreements</TabsTrigger>
              <TabsTrigger value="closing" className="text-xs">Closing Packet</TabsTrigger>
              <TabsTrigger value="payments" className="text-xs">Payments</TabsTrigger>
              <TabsTrigger value="activity" className="text-xs">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="pt-3" data-testid="tab-overview">
              <Card><CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                  <div><div className="text-[10px] text-slate-400 uppercase">Client</div><div>{client.full_name}</div></div>
                  <div><div className="text-[10px] text-slate-400 uppercase">Email</div><div className="truncate">{client.email || '—'}</div></div>
                  <div><div className="text-[10px] text-slate-400 uppercase">Phone</div><div>{client.phone || '—'}</div></div>
                  <div><div className="text-[10px] text-slate-400 uppercase">Mailing</div>
                    <div>{[client.street_address, client.city, client.state, client.zip_code].filter(Boolean).join(', ') || '—'}</div>
                  </div>
                  <div><div className="text-[10px] text-slate-400 uppercase">Deal Amount</div><div>{money(deal.price || deal.total_deal_price)}</div></div>
                  <div><div className="text-[10px] text-slate-400 uppercase">Source</div><div>{deal.deal_source || '—'}</div></div>
                </div>
                <div className="text-[11px] text-slate-500">Deal ID: <span className="font-mono">{deal.id}</span> · Client ID: <span className="font-mono">{client.id}</span></div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="ownership" className="pt-3" data-testid="tab-ownership">
              <div className="space-y-3">
                {ownerships.length === 0 ? (
                  <Card><CardContent className="p-6 text-center text-xs text-slate-500">
                    No ownership records yet.
                  </CardContent></Card>
                ) : ownerships.map((o, i) => (
                  <Card key={o.id}><CardContent className="p-4">
                    <div className="text-[10px] text-slate-400 uppercase mb-1">Timeshare {i + 1}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><b>{o.resort_name || 'Unknown / not provided'}</b></div>
                      <div>{o.resort_city ? `${o.resort_city}, ${o.resort_state || ''}` : ''}</div>
                      <div>Type: {o.ownership_type || '—'}</div>
                      <div>Points: {o.points_owned || '—'}</div>
                      <div>Maint. Fee: {money(o.annual_maintenance_fees)}</div>
                      <div>Balance: {money(o.loan_current_balance)}</div>
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="service_auth" className="pt-3" data-testid="tab-service-auth">
              <Card><CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold">Client Info and Service Authorization</div>
                    <div className="text-[11px] text-slate-500">
                      Authoritative source: <span className="font-mono">Client_Info_and_Service_Auth_v82926.pdf</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs"
                            onClick={openServiceAuthPreview}
                            data-testid="sa-preview-btn">
                      <FileText className="h-3.5 w-3.5 mr-1" /> Preview
                    </Button>
                    <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                            onClick={createRevision}
                            disabled={creating || !saContext?.ready_to_create}
                            data-testid="sa-create-btn">
                      {creating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                      Create
                    </Button>
                  </div>
                </div>
                {saContext && saContext.needs_input?.length > 0 && (
                  <MissingInfoEditor
                    client={client}
                    deal={deal}
                    ownerships={ownerships}
                    needs={saContext.needs_input}
                    onSaved={load}
                  />
                )}
                {revisions.length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold text-slate-600 mb-1">Revisions</div>
                    <div className="space-y-1">
                      {revisions.map(r => (
                        <div key={r.id} className="flex items-center justify-between text-[11px] bg-slate-50 border rounded px-2 py-1">
                          <div className="font-mono">{r.revision_number || shortId(r.id)}</div>
                          <div className="text-slate-500">{r.created_at}</div>
                          <Button size="sm" variant="ghost" className="h-6 text-[11px]"
                                  onClick={() => openRevision(r.id, r.revision_number)}
                                  data-testid={`sa-view-${shortId(r.id)}`}>
                            <ExternalLink className="h-3 w-3 mr-1" /> View
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="contract" className="pt-3" data-testid="tab-contract">
              <Card><CardContent className="p-6 text-center text-xs text-slate-500">
                Contract / Agreements inventory pending — see v3.21-1716 report for the authoritative contract source-of-truth recommendation.
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="closing" className="pt-3" data-testid="tab-closing">
              <Card><CardContent className="p-4 space-y-2">
                <div className="text-sm font-bold">Closing Packet</div>
                <div className="text-[11px] text-slate-500">
                  Authoritative source: <span className="font-mono">PTC_Complete_Closing_Packet_8.30.26.pdf</span> · 27 pages
                </div>
                {closingReady ? (
                  <Button size="sm" className="h-8 text-xs bg-cyan-600 hover:bg-cyan-700"
                          onClick={() => navigate(`/admin/closing-packet/${client.id}?deal=${deal.id}`)}
                          data-testid="closing-open-btn">
                    <FileText className="h-3.5 w-3.5 mr-1" /> Open Closing Packet
                  </Button>
                ) : (
                  <div className="border border-slate-200 bg-slate-50 rounded p-3 text-xs" data-testid="closing-not-ready">
                    <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Closing Packet Not Ready
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5 text-slate-600 text-[11px]">
                      {revisions.length === 0 && (
                        <li>Service Authorization has not been created yet.</li>
                      )}
                      {ownerships.length === 0 && (
                        <li>No Ownership records captured.</li>
                      )}
                      {needsInputCount > 0 && (
                        <li>{needsInputCount} required Service Auth field(s) missing.</li>
                      )}
                    </ul>
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="payments" className="pt-3" data-testid="tab-payments">
              <Card><CardContent className="p-6 text-center text-xs text-slate-500">
                Payments — coming in v3.21-1717.
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="activity" className="pt-3" data-testid="tab-activity">
              <Card><CardContent className="p-4">
                {activities.length === 0 ? (
                  <div className="text-xs text-slate-500 text-center">No activity yet.</div>
                ) : (
                  <div className="space-y-2">
                    {activities.slice(0, 20).map(a => (
                      <div key={a.id} className="text-[11px] border-b pb-1">
                        <div className="font-medium">{a.description || a.type}</div>
                        <div className="text-slate-500">{a.created_at || a.at}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-3">
          <Card><CardContent className="p-4 space-y-2">
            <div className="text-[10px] uppercase text-slate-400">Next Action</div>
            <div className="text-sm font-semibold" data-testid="workspace-next-action">
              {deal.next_action || 'Determine next step'}
            </div>
            {saContext?.ready_to_create && revisions.length === 0 && (
              <Button size="sm" className="h-8 text-xs w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={createRevision} disabled={creating}
                      data-testid="right-create-sa">
                {creating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                Create Service Authorization
              </Button>
            )}
          </CardContent></Card>

          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase text-slate-400 mb-1">Missing Information</div>
            {needsInputCount === 0 ? (
              <div className="text-xs text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> All required fields present
              </div>
            ) : (
              <div className="text-xs text-amber-700" data-testid="right-missing-info">
                {needsInputCount} field(s) needed
              </div>
            )}
          </CardContent></Card>

          <Card><CardContent className="p-4 space-y-1">
            <div className="text-[10px] uppercase text-slate-400 mb-1">Document Status</div>
            <div className="flex items-center justify-between text-xs">
              <span>Service Authorization</span>
              <Badge variant="outline" className="text-[10px]">
                {revisions.length ? `${revisions.length} revision(s)` : 'Not created'}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span>Closing Packet</span>
              <Badge variant="outline" className="text-[10px]">
                {closingReady ? 'Ready' : 'Not ready'}
              </Badge>
            </div>
          </CardContent></Card>

          <Card><CardContent className="p-4 space-y-1">
            <div className="text-[10px] uppercase text-slate-400 mb-1">Recent Activity</div>
            {activities.slice(0, 5).map(a => (
              <div key={a.id} className="text-[11px] border-b py-1">
                <div className="font-medium">{a.description || a.type}</div>
                <div className="text-slate-400">{a.created_at || a.at}</div>
              </div>
            ))}
            {activities.length === 0 && <div className="text-[11px] text-slate-400">None</div>}
          </CardContent></Card>
        </div>
      </div>

      {/* Authenticated PDF preview */}
      <AuthenticatedPdfViewer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={previewInfo.title}
        description={`${client.full_name} · ${deal.deal_number}`}
        fetchPath={previewInfo.path}
        filename={previewInfo.filename}
      />
    </div>
  );
}
